import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Card, CardTitle, Pill, Btn } from '../components/UI';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const C = {
  teal: '#314F5C', tealMid: '#6F8FA7', tealLight: '#AFE1F6', tealPale: '#E0F5FC',
  sage: '#819D84', red: '#c0392b', redBg: 'rgba(192,57,43,0.08)',
  amber: '#d97706', amberBg: 'rgba(217,119,6,0.08)',
  green: '#1b5e20', greenBg: 'rgba(27,94,32,0.08)',
  border: '#e9eef2', cardBg: '#ffffff', pageBg: '#f2f1ef',
};

function timeAgo(ts) {
  if (!ts) return '—';
  const m = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  return Math.floor(h / 24) + 'd ago';
}

function completionColor(pct) {
  if (pct >= 80) return C.green;
  if (pct >= 40) return C.amber;
  return C.red;
}

function passStatus(row) {
  if (!row.pass_type) return null;
  const expired = row.pass_expires_at && new Date(row.pass_expires_at) < new Date();
  return { label: expired ? 'Expired' : 'Active', color: expired ? C.red : C.green };
}

// ── Refund eligibility check ─────────────────────────────────────────────────
function checkRefundEligibility(row) {
  if (!row.pass_created_at) return { eligible: false, reason: 'No purchase record found.' };
  const hoursOld = (Date.now() - new Date(row.pass_created_at)) / 3600000;
  if (hoursOld > 24) {
    return { eligible: false, reason: `Purchase was ${Math.round(hoursOld)}h ago — outside the 24-hour cancellation window.` };
  }
  if (row.saved_count > 0) {
    return { eligible: false, reason: `App has been used — ${row.saved_count} location(s) saved.` };
  }
  return { eligible: true, reason: 'Within 24-hour window and app not used. Full refund eligible.' };
}

// ── Blood type chip options ───────────────────────────────────────────────────
const BLOOD_TYPES = ['A+','A−','B+','B−','AB+','AB−','O+','O−',"Don't know"];
const CONDITIONS   = ['Diabetes','Hypertension','Heart condition','Asthma / respiratory','Mental health','Autoimmune','Pregnancy','Thyroid condition','Epilepsy','None'];

// ── Small chip selector ───────────────────────────────────────────────────────
function ChipPicker({ options, value, multi, onChange }) {
  const sel = Array.isArray(value) ? value : (value ? [value] : []);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {options.map(o => {
        const on = sel.includes(o);
        return (
          <button key={o} onClick={() => {
            if (multi) {
              onChange(on ? sel.filter(x => x !== o) : [...sel, o]);
            } else {
              onChange(on ? [] : [o]);
            }
          }} style={{
            padding: '4px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
            background: on ? C.teal : 'rgba(49,79,92,0.06)',
            color: on ? '#e0f5fc' : C.teal,
            border: `0.5px solid ${on ? C.teal : C.border}`,
            fontFamily: F,
          }}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

// ── Confirm delete modal ──────────────────────────────────────────────────────
function DeleteModal({ profile, onConfirm, onCancel, saving }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.cardBg, borderRadius: 14, padding: '28px 28px 24px', maxWidth: 420, width: '90%', fontFamily: F }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.teal, marginBottom: 10 }}>Delete Travel Profile?</div>
        <div style={{ fontSize: 14, color: C.tealMid, lineHeight: 1.6, marginBottom: 22 }}>
          This will permanently delete the Travel Profile for <strong style={{ color: C.teal }}>{profile.email || profile.user_id}</strong>.
          This cannot be undone. The user's account and saved locations will not be affected.
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={onCancel} disabled={saving}>Cancel</Btn>
          <Btn variant="danger" onClick={onConfirm} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete profile'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Detail / edit panel ───────────────────────────────────────────────────────
function ProfilePanel({ profile, adminEmail, onClose, onSaved, onDeleted }) {
  const [mode, setMode]         = useState('view'); // 'view' | 'edit' | 'refund'
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [refundNote, setRefundNote] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [refundDone, setRefundDone] = useState(null);
  const [refundErr, setRefundErr]   = useState('');

  // Edit state — mirrors health_profiles columns
  const [eConditions, setEConditions]       = useState(profile.conditions ? profile.conditions.split(',').map(s => s.trim()).filter(Boolean) : []);
  const [eMedsAllerg, setEMedsAllerg]       = useState(profile.medications_allergies || '');
  const [eBloodType, setEBloodType]         = useState(profile.blood_type ? [profile.blood_type] : []);
  const [eEmergency, setEEmergency]         = useState(profile.emergency_contact || '');
  const [eWellness, setEWellness]           = useState(() => {
    try { return profile.wellness_data ? JSON.parse(profile.wellness_data) : {}; } catch (_e) { return {}; }
  });

  const refundCheck = checkRefundEligibility(profile);
  const isEligible  = refundCheck.eligible;

  async function handleSave() {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_update_health_profile', {
        p_user_id:               profile.user_id,
        p_conditions:            eConditions.join(', ') || null,
        p_medications_allergies: eMedsAllerg || null,
        p_blood_type:            eBloodType[0] || null,
        p_emergency_contact:     eEmergency || null,
        p_wellness_data:         JSON.stringify(eWellness),
      });
      if (error) throw error;

      // Log to activity log
      await supabase.from('admin_activity_log').insert({
        admin_email: adminEmail,
        action_type: 'profile_edited',
        target_id: profile.user_id,
        target_label: profile.email || profile.user_id,
        meta: { fields_updated: ['conditions','medications_allergies','blood_type','emergency_contact','wellness_data'] },
      }).then(() => {});

      setMode('view');
      onSaved();
    } catch (_e) {
      /* silently fail — rare */
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const { error } = await supabase.rpc('admin_delete_health_profile', {
        p_user_id: profile.user_id,
      });
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        admin_email: adminEmail,
        action_type: 'profile_deleted',
        target_id: profile.user_id,
        target_label: profile.email || profile.user_id,
        meta: {},
      }).then(() => {});
      onDeleted(profile.user_id);
    } catch (_e) {
      setDeleting(false);
      setShowDelete(false);
    }
  }

  async function handleLogRefund() {
    setSaving(true);
    setRefundErr('');
    const finalReason = isEligible
      ? 'Standard 24h window — no app usage'
      : overrideReason;
    if (!isEligible && !overrideReason.trim()) {
      setRefundErr('Please enter an override reason before logging.');
      setSaving(false);
      return;
    }
    try {
      const { data, error } = await supabase.rpc('admin_log_refund', {
        p_user_id:           profile.user_id,
        p_email:             profile.email || '',
        p_admin_email:       adminEmail,
        p_pass_type:         profile.pass_type || '',
        p_price_usd:         profile.price_usd || 0,
        p_stripe_payment_id: profile.stripe_payment_id || '',
        p_eligible:          isEligible || !!overrideReason.trim(),
        p_reason:            finalReason,
        p_notes:             refundNote,
      });
      if (error) throw error;
      setRefundDone({ id: data, stripePaymentId: profile.stripe_payment_id });
    } catch (_e) {
      setRefundErr('Failed to log refund. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const label = (text) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: C.tealMid, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, marginTop: 14 }}>{text}</div>
  );
  const field = (val) => (
    <div style={{ fontSize: 14, color: val ? C.teal : C.tealMid, fontStyle: val ? 'normal' : 'italic', lineHeight: 1.5, padding: '6px 0' }}>
      {val || 'Not set'}
    </div>
  );

  const ps = passStatus(profile);

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 480, background: C.cardBg, boxShadow: '-2px 0 20px rgba(0,0,0,0.12)', zIndex: 50, display: 'flex', flexDirection: 'column', fontFamily: F, overflowY: 'auto' }}>
      {showDelete && (
        <DeleteModal profile={profile} saving={deleting} onConfirm={handleDelete} onCancel={() => setShowDelete(false)} />
      )}

      {/* Header */}
      <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: C.cardBg, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.teal }}>{profile.email || 'Unknown user'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: C.tealMid, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: C.tealMid, fontFamily: 'monospace' }}>{profile.user_id}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {ps && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: ps.color === C.green ? C.greenBg : C.redBg, color: ps.color, fontWeight: 600 }}>{ps.label} · {profile.pass_type}</span>}
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'rgba(49,79,92,0.08)', color: C.teal, fontWeight: 600 }}>
            {profile.completion_pct ?? 0}% complete
          </span>
          {profile.saved_count > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'rgba(129,157,132,0.12)', color: C.sage, fontWeight: 600 }}>{profile.saved_count} saved</span>}
        </div>
        {/* Tab strip */}
        <div style={{ display: 'flex', gap: 0, marginTop: 14, borderBottom: `1px solid ${C.border}` }}>
          {[['view', 'Profile'], ['edit', 'Edit'], ['refund', 'Refund']].map(([m, lbl]) => (
            <button key={m} onClick={() => setMode(m)} style={{
              background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, fontFamily: F,
              color: mode === m ? C.teal : C.tealMid,
              borderBottom: mode === m ? `2px solid ${C.teal}` : '2px solid transparent',
              marginBottom: -1,
            }}>{lbl}</button>
          ))}
          <button onClick={() => setShowDelete(true)} style={{
            marginLeft: 'auto', background: 'none', border: 'none', padding: '8px 14px', cursor: 'pointer',
            fontSize: 12, color: C.red, fontFamily: F, fontWeight: 500,
          }}>Delete</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: '16px 22px 32px' }}>

        {/* ── VIEW ── */}
        {mode === 'view' && (
          <div>
            {label('Conditions')}
            {field(profile.conditions)}
            {label('Medications & allergies')}
            {field(profile.medications_allergies)}
            {label('Blood type')}
            {field(profile.blood_type)}
            {label('Emergency contact')}
            {field(profile.emergency_contact)}
            {label('Wellness data')}
            {(() => {
              try {
                const w = profile.wellness_data ? JSON.parse(profile.wellness_data) : null;
                if (!w || Object.keys(w).length === 0) return field(null);
                return (
                  <div style={{ fontSize: 13, color: C.teal, lineHeight: 1.8 }}>
                    {Object.entries(w).filter(([,v]) => v).map(([k, v]) => (
                      <div key={k}><span style={{ color: C.tealMid, fontSize: 12 }}>{k}:</span> {Array.isArray(v) ? v.join(', ') : String(v)}</div>
                    ))}
                  </div>
                );
              } catch (_e) { return field(null); }
            })()}
            <div style={{ height: 1, background: C.border, margin: '18px 0' }} />
            {label('Pass info')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 }}>
              {[
                ['Type', profile.pass_type || '—'],
                ['Price', profile.price_usd != null ? `$${profile.price_usd}` : '—'],
                ['Purchased', profile.pass_created_at ? new Date(profile.pass_created_at).toLocaleDateString() : '—'],
                ['Expires', profile.pass_expires_at ? new Date(profile.pass_expires_at).toLocaleDateString() : '—'],
                ['Stripe ID', profile.stripe_payment_id || '—'],
                ['Last updated', timeAgo(profile.updated_at)],
              ].map(([k, v]) => (
                <div key={k}>
                  <div style={{ fontSize: 11, color: C.tealMid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>{k}</div>
                  <div style={{ color: C.teal, fontFamily: k === 'Stripe ID' ? 'monospace' : F, fontSize: k === 'Stripe ID' ? 11 : 13 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── EDIT ── */}
        {mode === 'edit' && (
          <div>
            <div style={{ fontSize: 13, color: C.tealMid, marginBottom: 16, lineHeight: 1.5 }}>
              Editing will update this user's Travel Profile immediately and log the change to the Activity Log.
            </div>

            {label('Health conditions')}
            <ChipPicker options={CONDITIONS} value={eConditions} multi onChange={setEConditions} />

            {label('Blood type')}
            <ChipPicker options={BLOOD_TYPES} value={eBloodType} multi={false} onChange={setEBloodType} />

            {label('Medications & allergies')}
            <div style={{ fontSize: 11, color: C.tealMid, marginBottom: 6 }}>Comma-separated list</div>
            <textarea value={eMedsAllerg} onChange={e => setEMedsAllerg(e.target.value)}
              placeholder="e.g. Metformin, Insulin, allergic to Penicillin"
              rows={3} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: F, resize: 'none', boxSizing: 'border-box', color: C.teal }} />

            {label('Emergency contact')}
            <input value={eEmergency} onChange={e => setEEmergency(e.target.value)}
              placeholder="e.g. James Chen (Spouse) — +65 9123 4567"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: F, color: C.teal, boxSizing: 'border-box' }} />

            {label('Wellness — age')}
            <input type="number" value={eWellness.age || ''} onChange={e => setEWellness(p => ({ ...p, age: e.target.value }))}
              placeholder="e.g. 34"
              style={{ width: 120, padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: F, color: C.teal }} />

            {label('Wellness — fitness level')}
            <ChipPicker options={['Sedentary','Light','Moderate','Active','Athlete']}
              value={eWellness.fitnessLevel ? [eWellness.fitnessLevel] : []}
              multi={false}
              onChange={vals => setEWellness(p => ({ ...p, fitnessLevel: vals[0] || null }))} />

            <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
              <Btn variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Btn>
              <Btn variant="outline" onClick={() => setMode('view')} disabled={saving}>Cancel</Btn>
            </div>
          </div>
        )}

        {/* ── REFUND ── */}
        {mode === 'refund' && (
          <div>
            {refundDone ? (
              <div>
                <div style={{ background: C.greenBg, border: `1px solid ${C.green}`, borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.green, marginBottom: 4 }}>✓ Refund request logged</div>
                  <div style={{ fontSize: 13, color: C.green, lineHeight: 1.5 }}>
                    Request ID: <span style={{ fontFamily: 'monospace' }}>{refundDone.id}</span>
                  </div>
                </div>
                {refundDone.stripePaymentId && (
                  <a href={`https://dashboard.stripe.com/payments/${refundDone.stripePaymentId}`}
                    target="_blank" rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, background: C.teal, color: '#e0f5fc', fontSize: 13, fontWeight: 600, textDecoration: 'none', fontFamily: F }}>
                    Open in Stripe Dashboard →
                  </a>
                )}
                <div style={{ marginTop: 12, fontSize: 12, color: C.tealMid, lineHeight: 1.6 }}>
                  Process the refund manually in Stripe, then mark this request as resolved in the refund log.
                </div>
              </div>
            ) : (
              <div>
                {/* Eligibility verdict */}
                <div style={{ background: isEligible ? C.greenBg : C.redBg, border: `1px solid ${isEligible ? C.green : C.red}`, borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: isEligible ? C.green : C.red, marginBottom: 4 }}>
                    {isEligible ? '✓ Eligible for refund' : '✗ Not eligible under standard policy'}
                  </div>
                  <div style={{ fontSize: 13, color: isEligible ? C.green : C.red, lineHeight: 1.5 }}>{refundCheck.reason}</div>
                </div>

                {/* Pass + payment info */}
                <div style={{ background: 'rgba(49,79,92,0.04)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: C.tealMid }}>Pass type</span>
                    <span style={{ color: C.teal, fontWeight: 500 }}>{profile.pass_type || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: C.tealMid }}>Amount paid</span>
                    <span style={{ color: C.teal, fontWeight: 500 }}>{profile.price_usd != null ? `$${profile.price_usd}` : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: C.tealMid }}>Stripe payment ID</span>
                    <span style={{ color: C.teal, fontFamily: 'monospace', fontSize: 11 }}>{profile.stripe_payment_id || '—'}</span>
                  </div>
                </div>

                {/* Override section (only shown when ineligible) */}
                {!isEligible && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.amber, marginBottom: 8 }}>Exception override</div>
                    <div style={{ fontSize: 12, color: C.tealMid, marginBottom: 8, lineHeight: 1.5 }}>
                      Override eligibility only for: technical failure (app charged but never granted access) or duplicate charge. Required field.
                    </div>
                    <select value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: F, color: C.teal, background: '#fff', boxSizing: 'border-box' }}>
                      <option value="">Select exception reason…</option>
                      <option value="Technical failure — access never granted">Technical failure — access never granted</option>
                      <option value="Duplicate charge — payment processing error">Duplicate charge — payment processing error</option>
                    </select>
                  </div>
                )}

                {/* Notes */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.tealMid, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Internal notes (optional)</div>
                  <textarea value={refundNote} onChange={e => setRefundNote(e.target.value)}
                    placeholder="Add any context for the refund record…"
                    rows={3} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: F, resize: 'none', boxSizing: 'border-box', color: C.teal }} />
                </div>

                {refundErr && <div style={{ fontSize: 13, color: C.red, marginBottom: 12 }}>{refundErr}</div>}

                <Btn variant={isEligible ? 'primary' : 'outline'} onClick={handleLogRefund} disabled={saving || (!isEligible && !overrideReason)}>
                  {saving ? 'Logging…' : 'Log refund request'}
                </Btn>
                <div style={{ marginTop: 10, fontSize: 12, color: C.tealMid, lineHeight: 1.5 }}>
                  This logs the request and opens Stripe for manual processing. The refund is not automatically issued.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function TravelProfiles() {
  const [profiles, setProfiles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all'); // all | complete | incomplete | active | expired
  const [selected, setSelected]   = useState(null);
  const [adminEmail, setAdminEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase.rpc('admin_get_travel_profiles');
      setProfiles(data || []);
    } catch (_e) { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email) setAdminEmail(data.user.email);
    });
  }, [load]);

  // Re-open selected with fresh data after edit
  function handleSaved() {
    load();
    // Update selected profile from fresh data
  }

  function handleDeleted(userId) {
    setProfiles(prev => prev.filter(p => p.user_id !== userId));
    setSelected(null);
  }

  // Filter + search
  const visible = profiles.filter(p => {
    const q = search.toLowerCase();
    if (q && !p.email?.toLowerCase().includes(q) && !p.user_id?.includes(q)) return false;
    const now = new Date();
    const expired = p.pass_expires_at && new Date(p.pass_expires_at) < now;
    if (filter === 'active' && (!p.pass_type || expired)) return false;
    if (filter === 'expired' && !expired) return false;
    if (filter === 'complete' && (p.completion_pct || 0) < 80) return false;
    if (filter === 'incomplete' && (p.completion_pct || 0) >= 80) return false;
    return true;
  });

  // Stats
  const totalComplete  = profiles.filter(p => (p.completion_pct || 0) >= 80).length;
  const totalActive    = profiles.filter(p => p.pass_type && new Date(p.pass_expires_at) > new Date()).length;
  const avgCompletion  = profiles.length > 0 ? Math.round(profiles.reduce((a, p) => a + (p.completion_pct || 0), 0) / profiles.length) : 0;

  return (
    <div style={{ fontFamily: F }}>
      {selected && (
        <ProfilePanel
          profile={selected}
          adminEmail={adminEmail}
          onClose={() => setSelected(null)}
          onSaved={() => { handleSaved(); setSelected(null); }}
          onDeleted={handleDeleted}
        />
      )}

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total profiles',   value: loading ? '…' : profiles.length },
          { label: 'Avg completion',   value: loading ? '…' : avgCompletion + '%' },
          { label: 'Fully complete',   value: loading ? '…' : totalComplete, accent: '#819D84' },
          { label: 'Active pass',      value: loading ? '…' : totalActive,   accent: '#314F5C' },
        ].map(({ label: lbl, value, accent }) => (
          <div key={lbl} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.04)', borderTop: `3px solid ${accent || '#6F8FA7'}` }}>
            <div style={{ fontSize: 11, color: C.tealMid, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{lbl}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: C.teal }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by email or user ID…"
          style={{ flex: 1, minWidth: 220, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: F, color: C.teal }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          {[['all','All'], ['active','Active pass'], ['expired','Expired'], ['complete','≥80% complete'], ['incomplete','Incomplete']].map(([v, lbl]) => (
            <button key={v} onClick={() => setFilter(v)} style={{
              padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: F,
              background: filter === v ? C.teal : '#fff',
              color: filter === v ? '#e0f5fc' : C.tealMid,
              border: `1px solid ${filter === v ? C.teal : C.border}`,
            }}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 700 }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px', gap: 0, padding: '10px 16px', background: 'rgba(49,79,92,0.04)', borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.tealMid, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              <div>User</div>
              <div>Completion</div>
              <div>Blood type</div>
              <div>Pass</div>
              <div>Updated</div>
              <div>Saves</div>
            </div>

            {loading && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: C.tealMid, fontSize: 13 }}>Loading profiles…</div>
            )}

            {!loading && visible.length === 0 && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: C.tealMid, fontSize: 13 }}>
                {profiles.length === 0 ? 'No Travel Profiles found.' : 'No profiles match the current filter.'}
              </div>
            )}

            {!loading && visible.map((p, i) => {
              const ps = passStatus(p);
              const pct = p.completion_pct || 0;
              return (
                <div key={p.user_id}
                  onClick={() => setSelected(p)}
                  style={{
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px',
                    padding: '12px 16px', cursor: 'pointer', fontSize: 13,
                    borderBottom: i < visible.length - 1 ? `1px solid ${C.border}` : 'none',
                    background: selected?.user_id === p.user_id ? 'rgba(49,79,92,0.04)' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (selected?.user_id !== p.user_id) e.currentTarget.style.background = 'rgba(49,79,92,0.03)'; }}
                  onMouseLeave={e => { if (selected?.user_id !== p.user_id) e.currentTarget.style.background = 'transparent'; }}
                >
                  {/* Email */}
                  <div>
                    <div style={{ color: C.teal, fontWeight: 500 }}>{p.email || '—'}</div>
                    <div style={{ fontSize: 11, color: C.tealMid, fontFamily: 'monospace', marginTop: 2 }}>{p.user_id?.slice(0, 18)}…</div>
                  </div>
                  {/* Completion */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 48, height: 6, background: 'rgba(49,79,92,0.1)', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: 6, width: `${pct}%`, background: completionColor(pct), borderRadius: 999 }} />
                    </div>
                    <span style={{ color: completionColor(pct), fontWeight: 600, fontSize: 12 }}>{pct}%</span>
                  </div>
                  {/* Blood type */}
                  <div style={{ color: p.blood_type ? C.teal : C.tealMid, fontStyle: p.blood_type ? 'normal' : 'italic' }}>
                    {p.blood_type || '—'}
                  </div>
                  {/* Pass */}
                  <div>
                    {ps ? (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: ps.color === C.green ? C.greenBg : C.redBg, color: ps.color, fontWeight: 600 }}>
                        {ps.label}
                      </span>
                    ) : (
                      <span style={{ color: C.tealMid, fontStyle: 'italic', fontSize: 12 }}>No pass</span>
                    )}
                  </div>
                  {/* Updated */}
                  <div style={{ color: C.tealMid, fontSize: 12 }}>{timeAgo(p.updated_at)}</div>
                  {/* Saves */}
                  <div style={{ color: C.teal, fontWeight: 500 }}>{p.saved_count || 0}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <div style={{ marginTop: 10, fontSize: 12, color: C.tealMid }}>
        {visible.length} of {profiles.length} profiles shown
      </div>
    </div>
  );
}
